import type {ReactNode} from 'react'
import {exhaustive} from '@freckle/exhaustive'
import {
  addIfClassNames,
  addMaybeClassName,
} from '@freckle/educator-entities/ts/common/helpers/classnames'
import {Text} from '@freckle/educator-materials/ts/components/typography'
import {
  progressBarWrapper,
  lgProgressBar,
  screenReaderOnly,
  progressBar,
  labelText,
  defaultProgressBar,
  successProgressBar,
  primaryProgressBar,
  warningProgressBar,
  dangerProgressBar,
  infoProgressBar,
  staticLabelPositionStyle,
} from './progress-bar.module.scss'

export type StyleT = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info'

type LabelPosition = 'dynamic' | 'static'

export type ProgressBarSizeT = 'md' | 'lg'

type Props = {
  value: number
  label: ReactNode
  labelPosition?: LabelPosition
  showLabel?: boolean
  min?: number
  max?: number
  style?: StyleT
  size?: ProgressBarSizeT
  wrapperAddClass?: string
  className?: string
  filledWidth?: {
    width: string
  }
}

function ProgressBar(props: Props) {
  const {
    label,
    showLabel = false,
    labelPosition = 'dynamic',
    value,
    min = 0,
    max = 100,
    wrapperAddClass,
    className,
    style = 'success',
    size = 'md',
    filledWidth,
  } = props

  const labelClass = addIfClassNames([
    [true, labelText],
    [labelPosition === 'static', staticLabelPositionStyle],
    [!showLabel, screenReaderOnly],
  ])

  const newClassName = addIfClassNames([
    [true, getStyleClassName(style)],
    [size === 'lg', lgProgressBar],
    [className !== undefined, className],
  ])

  const computedWidth = ((value - min) / (max - min)) * 100

  const widthAttr = filledWidth ?? {width: `${computedWidth}%`}

  return (
    <div
      className={addMaybeClassName(progressBarWrapper, wrapperAddClass)}
      data-test={'progress-bar-wrapper'}
      role="progressbar"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
    >
      <figure
        role="progressbar"
        className={`${progressBar} ${newClassName}`}
        aria-valuenow={value}
        aria-valuemin={min}
        aria-valuemax={max}
        style={{...widthAttr}}
      >
        <Text style="caption-1" className={labelClass} htmlElement="h1">
          {label}
        </Text>
      </figure>
    </div>
  )
}

function getStyleClassName(style: StyleT): string {
  switch (style) {
    case 'default':
      return defaultProgressBar
    case 'success':
      return successProgressBar
    case 'primary':
      return primaryProgressBar
    case 'warning':
      return warningProgressBar
    case 'danger':
      return dangerProgressBar
    case 'info':
      return infoProgressBar
    default:
      return exhaustive(style, 'StyleT')
  }
}

export {ProgressBar}
